import { Popover as AntPopover, type PopoverProps as AntdPopoverProps } from "antd/es";
import isFunction from "lodash/isFunction";
import React, { type FC, useState } from "react";

import { cx } from "~/tools/class-variance";
import { noop } from "~/tools/noop";
import { useOn } from "~/tools/use-on";

export const GLOBAL_POPOVER_CLASSNAME = "sts-popover";

const DEBOUNCE_MS = 250;

const ANT_POPOVER_CLASSES = [".ant-dropdown", ".ant-popover", ".ant-picker-dropdown"];

export type PopoverContent = React.ReactElement | boolean;
export type PopoverContentFunction = (requestClose: () => void, isOpen: boolean) => PopoverContent;

export interface PopoverProps
	extends Omit<AntdPopoverProps, "visible" | "defaultVisible" | "overlayClassName" | "content"> {
	className?: string;
	content: React.ReactNode | PopoverContentFunction;
	defaultOpen?: boolean;
	disabled?: boolean;
	open?: boolean;
	onToggle?: (isOpen: boolean) => void;
	closeOnScrollOrResize?: boolean;
}

export const Popover: FC<PopoverProps> = ({
	className,
	content,
	defaultOpen = false,
	disabled,
	open: controlledOpen,
	onToggle = noop,
	closeOnScrollOrResize = true,
	placement = "bottomLeft",
	children,
	onVisibleChange,
	...props
}) => {
	const [internalOpen, setInternalOpen] = useState(defaultOpen);

	const isOpen = disabled ? false : (controlledOpen ?? internalOpen);

	const handleToggle = (nextOpen: boolean): void => {
		if (disabled) {
			return;
		}
		setInternalOpen(nextOpen);
		if (controlledOpen !== nextOpen) {
			onToggle(nextOpen);
		}
	};

	const handleClose = (): void => handleToggle(false);

	const handleVisibleChange = (visible: boolean): void => {
		if (disabled) {
			return;
		}
		onVisibleChange?.(visible);
		handleToggle(visible);
	};

	const handleWindowScrollResize = (_node: Window, event: Event): void => {
		const { target } = event;
		if (target instanceof HTMLElement && !target.closest(ANT_POPOVER_CLASSES.join(", "))) {
			handleClose();
		}
	};

	// biome-ignore lint/style/noRestrictedGlobals: resize events fire on window
	useOn("resize", handleWindowScrollResize, { node: window, enabled: closeOnScrollOrResize, throttle: DEBOUNCE_MS });
	// biome-ignore lint/style/noRestrictedGlobals: wheel events need to be captured on window
	useOn("wheel", handleWindowScrollResize, { node: window, enabled: closeOnScrollOrResize, throttle: DEBOUNCE_MS });

	const renderedContent = isFunction(content) ? content(handleClose, isOpen) : content;

	return (
		<AntPopover
			{...props}
			content={renderedContent}
			onVisibleChange={handleVisibleChange}
			overlayClassName={cx(
				className,
				"[&.padding-0]:p-0",
				"[&_.ant-popover-content]:bg-inherit",
				"[&_.ant-popover-arrow]:hidden",
				"[&_.ant-popover-inner-content]:rounded-xs [&_.ant-popover-inner-content]:bg-inherit [&_.ant-popover-inner-content]:p-0 [&_.ant-popover-inner-content]:text-inherit",
				"[&_.ant-popover-inner-content>*]:rounded-xs",
			)}
			placement={placement}
			visible={isOpen}
		>
			{children}
		</AntPopover>
	);
};
